import React, { useMemo, useState } from "react";
import {
  bucketAlertsByHourWeek,
  bucketSessionsByHourWeek,
} from "./unitEventsUtils";

type Range = "week" | "month" | "3month" | "year" | "all";
type Mode = "alerts" | "sessions";

const RANGES: ReadonlyArray<readonly [Range, string]> = [
  ["week", "Past Week"],
  ["month", "Past Month"],
  ["3month", "Past 3 Months"],
  ["year", "Past Year"],
  ["all", "All Time"],
];

const MODES: ReadonlyArray<readonly [Mode, string]> = [
  ["alerts", "Alerts"],
  ["sessions", "Cooking Sessions"],
];

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

type Props = {
  records: any[];
  historyRecords?: any[];
  sessions?: any[];
  historySessions?: any[];
  defaultRange?: Range;
  defaultMode?: Mode;
};

export default function AlertHeatmapCard({
  records,
  historyRecords,
  sessions,
  historySessions,
  defaultRange = "month",
  defaultMode = "alerts",
}: Props) {
  const [range, setRange] = useState<Range>(defaultRange);
  const [mode, setMode] = useState<Mode>(defaultMode);

  const needsHistory = range === "year" || range === "all";
  const sourceRecords = useMemo(
    () => (needsHistory && historyRecords ? historyRecords : records),
    [needsHistory, historyRecords, records]
  );
  const sourceSessions = useMemo(
    () =>
      needsHistory && historySessions ? historySessions : sessions ?? [],
    [needsHistory, historySessions, sessions]
  );

  const matrix = useMemo(() => {
    return mode === "alerts"
      ? bucketAlertsByHourWeek(sourceRecords, range)
      : bucketSessionsByHourWeek(sourceSessions, range);
  }, [mode, sourceRecords, sourceSessions, range]);

  const max = useMemo(() => {
    let m = 0;
    for (const row of matrix) {
      for (const v of row) if (v > m) m = v;
    }
    return m;
  }, [matrix]);

  const baseRgb = mode === "alerts" ? "187,218,98" : "25,118,210";

  const cellColor = (value: number) => {
    if (value === 0) return "rgba(0,0,0,0.04)";
    if (max <= 1) return `rgba(${baseRgb},0.85)`;
    const t = Math.log(value + 1) / Math.log(max + 1);
    const alpha = 0.18 + t * 0.72;
    return `rgba(${baseRgb},${alpha.toFixed(3)})`;
  };

  const noun = mode === "alerts" ? "alert" : "session";
  const hours = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold">Activity Tracking</h2>
          <p className="text-ink-3 text-xs mt-1">
            Day of week × hour of day. Darker = more {mode === "alerts" ? "alerts" : "sessions"}.
            Hover any cell for the exact count.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div
            role="group"
            aria-label="Heatmap mode"
            className="inline-flex rounded-control border border-black/10 bg-black/[0.03] p-0.5 text-sm"
          >
            {MODES.map(([key, label]) => {
              const active = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  className={[
                    "px-3 py-1.5 rounded-control transition-colors",
                    active
                      ? "bg-primary/20 text-ink font-semibold border border-primary/45"
                      : "text-ink-2 hover:text-ink",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div
            role="group"
            aria-label="Heatmap range"
            className="inline-flex rounded-control border border-black/10 bg-black/[0.03] p-0.5 text-sm"
          >
            {RANGES.map(([key, label]) => {
              const active = range === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRange(key)}
                  className={[
                    "px-3 py-1.5 rounded-control transition-colors",
                    active
                      ? "bg-primary/20 text-ink font-semibold border border-primary/45"
                      : "text-ink-2 hover:text-ink",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {max === 0 ? (
        <div className="text-ink-3 text-sm py-12 text-center">
          No {mode === "alerts" ? "alerts" : "cooking sessions"} in this range.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: "auto repeat(24, minmax(18px, 1fr))",
            }}
          >
            <div />
            {hours.map((h) => (
              <div
                key={`h-${h}`}
                className="text-[10px] text-ink-3 text-center"
              >
                {h % 3 === 0 ? h : ""}
              </div>
            ))}
            {DOW_LABELS.map((dow, i) => (
              <React.Fragment key={dow}>
                <div className="text-xs text-ink-3 pr-2 flex items-center">
                  {dow}
                </div>
                {hours.map((h) => {
                  const v = matrix[i][h];
                  return (
                    <div
                      key={`${dow}-${h}`}
                      title={`${dow} ${String(h).padStart(2, "0")}:00, ${v} ${noun}${v === 1 ? "" : "s"}`}
                      className="aspect-square rounded-[4px] border border-black/[0.04] cursor-help transition-shadow hover:shadow-[0_0_0_2px_rgba(187,218,98,0.5)]"
                      style={{ backgroundColor: cellColor(v) }}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
